import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, ShoppingBag, ExternalLink, Copy, Check, Pencil, Eye, Send, TrendingUp, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getSitePages, ensureUserPages, generateSlug } from '../services/siteService';
import { getPageStatsForWebsite } from '../services/analyticsService';
import type { SitePage, SitePageType } from '../types/site';
import { PAGE_TYPE_LABELS } from '../types/site';
import { logger } from '../lib/logger';

const PAGE_ICONS: Record<SitePageType, typeof Briefcase> = {
  BUSINESS: Briefcase,
  PRODUCTS: ShoppingBag,
};

const PAGE_DESCRIPTIONS: Record<SitePageType, string> = {
  BUSINESS: 'Rekrutiere neue Partner für dein Team',
  PRODUCTS: 'Biete Produktberatungen an',
};

interface PageStats {
  views: number;
  submissions: number;
  conversion: number;
}

export function SitePages() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState<SitePage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [pageStats, setPageStats] = useState<Record<SitePageType, PageStats>>({
    BUSINESS: { views: 0, submissions: 0, conversion: 0 },
    PRODUCTS: { views: 0, submissions: 0, conversion: 0 },
  });

  useEffect(() => {
    if (user && profile) {
      initializePages();
    }
  }, [user, profile]);

  const initializePages = async () => {
    if (!user || !profile) return;
    try {
      const slug = generateSlug(profile.full_name || user.email || 'user');
      await ensureUserPages(user.id, slug);
      const data = await getSitePages(user.id);
      setPages(data);

      const [businessStats, productsStats] = await Promise.all([
        getPageStatsForWebsite(user.id, 'BUSINESS'),
        getPageStatsForWebsite(user.id, 'PRODUCTS'),
      ]);
      setPageStats({
        BUSINESS: businessStats,
        PRODUCTS: productsStats,
      });
    } catch (error) {
      logger.error('Error loading pages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyPublicUrl = async (page: SitePage) => {
    const urlPath = page.type === 'BUSINESS' ? 'business' : 'products';
    const url = `${window.location.origin}/u/${page.slug}/${urlPath}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(page.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getPublicUrl = (page: SitePage) => {
    const urlPath = page.type === 'BUSINESS' ? 'business' : 'products';
    return `/u/${page.slug}/${urlPath}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] dark:text-white">Meine Seiten</h1>
        <p className="text-[#6B6B6B] dark:text-slate-400 mt-1">
          Verwalte deine Landingpages
        </p>
      </div>

      <div data-tour="page-cards" className="grid md:grid-cols-2 gap-6">
        {pages.map((page) => {
          const Icon = PAGE_ICONS[page.type];
          return (
            <div
              key={page.id}
              className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {PAGE_TYPE_LABELS[page.type]}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        {PAGE_DESCRIPTIONS[page.type]}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                      page.is_published
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-gray-100 dark:bg-slate-600 text-gray-600 dark:text-slate-300'
                    }`}
                  >
                    {page.is_published ? 'Veröffentlicht' : 'Entwurf'}
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Titel</p>
                    <p className="text-sm text-gray-900 dark:text-white">{page.title}</p>
                  </div>

                  {page.is_published && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Öffentliche URL</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                          {getPublicUrl(page)}
                        </code>
                        <button
                          onClick={() => copyPublicUrl(page)}
                          className="p-1.5 text-gray-400 hover:text-primary transition-colors"
                          title="Link kopieren"
                        >
                          {copiedId === page.id ? (
                            <Check className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <a
                          href={getPublicUrl(page)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-primary transition-colors"
                          title="Seite öffnen"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
                  <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-3">Letzte 7 Tage</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-gray-400 dark:text-slate-500 mb-1">
                        <Eye className="w-3.5 h-3.5" />
                      </div>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">{pageStats[page.type].views}</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">Aufrufe</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-gray-400 dark:text-slate-500 mb-1">
                        <Send className="w-3.5 h-3.5" />
                      </div>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">{pageStats[page.type].submissions}</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">Anfragen</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-gray-400 dark:text-slate-500 mb-1">
                        <TrendingUp className="w-3.5 h-3.5" />
                      </div>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">{pageStats[page.type].conversion}%</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">Conv.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 dark:bg-slate-700 border-t border-gray-100 dark:border-slate-600 flex items-center gap-3">
                <button
                  data-tour="edit-button"
                  onClick={() => navigate(`/site/page/${page.type.toLowerCase()}`)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium"
                >
                  <Pencil className="w-4 h-4" />
                  Bearbeiten
                </button>
                <Link
                  to={`/analytics?page=${page.type}`}
                  className="px-4 py-2.5 bg-white dark:bg-slate-600 border border-gray-200 dark:border-slate-500 text-gray-700 dark:text-white rounded-xl hover:bg-gray-50 dark:hover:bg-slate-500 transition-colors font-medium flex items-center gap-2"
                >
                  <BarChart3 className="w-4 h-4" />
                  Details
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
